/**
 * Beau-fils Store Locator — Vanilla JS
 * No dependencies. Works with Google Maps API loaded async via section settings.
 */

(function () {
  'use strict';

  function qs(selector, root) { return (root || document).querySelector(selector); }
  function qsa(selector, root) { return Array.from((root || document).querySelectorAll(selector)); }

  function normalize(str) {
    return String(str || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  }

  const ALLOWED_COUNTRIES = ['belgique', 'france', 'luxembourg'];

  function normalizeCountry(raw) {
    const n = normalize(raw);
    if (n === 'belgium' || n === 'belgie' || n === 'belgien' || n === 'belgique') return 'Belgique';
    if (n === 'france')     return 'France';
    if (n === 'luxembourg') return 'Luxembourg';
    return raw;
  }

  // ─── StoreLocator ────────────────────────────────────────────────────────────

  function StoreLocator(sectionEl) {
    const sectionId   = sectionEl.dataset.sectionId;
    const varName     = 'slStores_' + sectionId.replace(/-/g, '_');
    const stores      = window[varName] || [];

    const searchEl    = qs('#sl-search-'     + sectionId);
    const countryWrap = qs('#sl-country-'    + sectionId);
    const cityWrap    = qs('#sl-city-'       + sectionId);
    const locateBtn   = qs('#sl-locate-'     + sectionId);
    const listEl      = qs('#sl-list-'       + sectionId);
    const noResults   = qs('#sl-no-results-' + sectionId);
    const mapEl       = qs('#sl-map-'        + sectionId);
    const cards       = qsa('.sl-card', listEl);

    let map        = null;
    let markers    = [];
    let infoWindow = null;
    let activeCard = null;

    // Normalize country names in store data and card attributes
    stores.forEach((s) => { s.country = normalizeCountry(s.country || ''); });
    cards.forEach((card) => {
      card.dataset.country = normalize(normalizeCountry(card.dataset.country || ''));
    });

    let selectedCountries = new Set();
    let selectedCities    = new Set();

    // City index per country
    const allCities = {};
    stores.forEach((s) => {
      const c = s.country || '';
      if (!allCities[c]) allCities[c] = [];
      if (s.city && !allCities[c].includes(s.city)) allCities[c].push(s.city);
    });

    // ─── Checkbox panels ───────────────────────────────────────────────────────

    function buildPanel(wrap, items, selectedSet, onChange) {
      if (!wrap) return;
      const label = wrap.dataset.label || '';
      wrap.innerHTML = '';

      const btn = document.createElement('button');
      btn.className = 'sl-filter-btn';
      btn.type = 'button';
      btn.innerHTML =
        '<span class="sl-filter-btn-label"></span>' +
        '<svg class="sl-filter-arrow" viewBox="0 0 10 6" xmlns="http://www.w3.org/2000/svg">' +
        '<path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round"/></svg>';
      wrap.appendChild(btn);

      const panel = document.createElement('div');
      panel.className = 'sl-filter-panel';
      wrap.appendChild(panel);

      items.forEach((item) => {
        const uid = 'sl-chk-' + sectionId + '-' + normalize(item).replace(/\s+/g, '-');
        const row = document.createElement('label');
        row.className = 'sl-filter-item';
        row.htmlFor = uid;

        const chk = document.createElement('input');
        chk.type = 'checkbox';
        chk.id = uid;
        chk.value = item;
        chk.checked = selectedSet.has(item);
        chk.addEventListener('change', () => {
          if (chk.checked) selectedSet.add(item);
          else selectedSet.delete(item);
          updateBadge(btn, selectedSet, label);
          onChange();
        });

        row.appendChild(chk);
        row.appendChild(document.createTextNode('\u00a0' + item));
        panel.appendChild(row);
      });

      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        wrap.classList.toggle('is-open');
        qsa('.sl-filter-wrap.is-open', sectionEl).forEach((w) => {
          if (w !== wrap) w.classList.remove('is-open');
        });
      });

      updateBadge(btn, selectedSet, label);
    }

    function updateBadge(btn, selectedSet, label) {
      const span = btn.querySelector('.sl-filter-btn-label');
      if (span) span.textContent = selectedSet.size > 0 ? label + ' (' + selectedSet.size + ')' : label;
    }

    // Country panel — only Belgique / France / Luxembourg
    const countryList = [...new Set(
      stores.map(s => s.country).filter(c => ALLOWED_COUNTRIES.includes(normalize(c)))
    )].sort();

    buildPanel(countryWrap, countryList, selectedCountries, () => {
      rebuildCityPanel();
      applyFilters();
    });

    function rebuildCityPanel() {
      let cityList;
      if (selectedCountries.size > 0) {
        const seen = new Set();
        cityList = [];
        selectedCountries.forEach((country) => {
          (allCities[country] || []).forEach((city) => {
            if (!seen.has(city)) { seen.add(city); cityList.push(city); }
          });
        });
      } else {
        cityList = [...new Set(stores.map(s => s.city).filter(Boolean))];
      }
      cityList.sort();
      selectedCities.forEach((c) => { if (!cityList.includes(c)) selectedCities.delete(c); });
      buildPanel(cityWrap, cityList, selectedCities, applyFilters);
    }

    rebuildCityPanel();

    // Close panels on outside click
    document.addEventListener('click', () => {
      qsa('.sl-filter-wrap.is-open', sectionEl).forEach((w) => w.classList.remove('is-open'));
    });
    sectionEl.addEventListener('click', (e) => e.stopPropagation());

    // ─── Filter Logic ──────────────────────────────────────────────────────────

    function applyFilters() {
      const query = normalize(searchEl ? searchEl.value : '');
      let visibleCount = 0;

      cards.forEach((card) => {
        const name     = normalize(card.dataset.name);
        const cardCity = normalize(card.dataset.city);
        const cardCtry = normalize(card.dataset.country);

        const matchesQuery   = !query || name.includes(query) || cardCity.includes(query);
        const matchesCountry = selectedCountries.size === 0 ||
          [...selectedCountries].some(c => normalize(c) === cardCtry);
        const matchesCity    = selectedCities.size === 0 ||
          [...selectedCities].some(c => normalize(c) === cardCity);

        const visible = matchesQuery && matchesCountry && matchesCity;
        card.classList.toggle('is-hidden', !visible);
        if (visible) visibleCount++;
      });

      if (noResults) noResults.classList.toggle('hidden', visibleCount > 0);

      if (markers.length > 0) {
        cards.forEach((card, i) => {
          if (markers[i]) markers[i].setVisible(!card.classList.contains('is-hidden'));
        });
      }
    }

    // ─── Events ────────────────────────────────────────────────────────────────

    if (searchEl) {
      searchEl.addEventListener('input', applyFilters);
      searchEl.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') { searchEl.value = ''; applyFilters(); }
      });
    }

    if (locateBtn) locateBtn.addEventListener('click', () => geolocateUser(null));

    cards.forEach((card) => {
      card.addEventListener('click', () => {
        const id  = parseInt(card.dataset.id, 10);
        const lat = parseFloat(card.dataset.lat);
        const lng = parseFloat(card.dataset.lng);
        activateCard(card);
        if (map && !isNaN(lat) && !isNaN(lng)) {
          map.panTo({ lat, lng });
          map.setZoom(14);
          if (markers[id]) openInfoWindowOn(markers[id], stores[id]);
        }
      });
    });

    function activateCard(card) {
      if (activeCard) activeCard.classList.remove('is-active');
      card.classList.add('is-active');
      activeCard = card;
      card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    // ─── Geolocation ──────────────────────────────────────────────────────────

    function geolocateUser(callback) {
      if (!navigator.geolocation) { if (callback) callback(false); return; }
      if (locateBtn) locateBtn.disabled = true;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const userLat = pos.coords.latitude;
          const userLng = pos.coords.longitude;
          if (map) { map.panTo({ lat: userLat, lng: userLng }); map.setZoom(10); }

          let minDist = Infinity, closest = null, closestCard = null;
          stores.forEach((store, i) => {
            if (store.lat == null || store.lng == null) return;
            const dist = haversine(userLat, userLng, store.lat, store.lng);
            if (dist < minDist) { minDist = dist; closest = store; closestCard = cards[i]; }
          });

          if (closestCard) {
            activateCard(closestCard);
            if (map && closest) { map.panTo({ lat: closest.lat, lng: closest.lng }); map.setZoom(12); }
          }
          if (locateBtn) locateBtn.disabled = false;
          if (callback) callback(true);
        },
        () => { if (locateBtn) locateBtn.disabled = false; if (callback) callback(false); },
        { timeout: 5000 }
      );
    }

    function haversine(lat1, lon1, lat2, lon2) {
      const R = 6371;
      const dLat = deg2rad(lat2 - lat1), dLon = deg2rad(lon2 - lon1);
      const a = Math.sin(dLat / 2) ** 2 +
                Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) ** 2;
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }
    function deg2rad(deg) { return deg * (Math.PI / 180); }

    // ─── Google Maps ──────────────────────────────────────────────────────────

    function markerIcon(active) {
      return {
        path: google.maps.SymbolPath.CIRCLE,
        scale: active ? 10 : 8,
        fillColor: '#0033a0',
        fillOpacity: active ? 1 : 0.85,
        strokeColor: '#ffffff',
        strokeWeight: 2,
      };
    }

    function openInfoWindowOn(marker, store) {
      if (!infoWindow) return;
      markers.forEach((m) => { if (m) m.setIcon(markerIcon(false)); });
      marker.setIcon(markerIcon(true));
      const i18n   = window.slI18n || {};
      const mapsUrl = (store.lat && store.lng)
        ? 'https://www.google.com/maps/dir/?api=1&destination=' + store.lat + ',' + store.lng
        : 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent((store.address || '') + ' ' + (store.city || ''));

      infoWindow.setContent(
        '<div class="sl-info-window">'
        + '<strong>' + escHtml(store.name) + '</strong>'
        + '<p>' + escHtml(store.address) + '<br/>' + escHtml(store.city)
        + (store.country ? ', ' + escHtml(store.country) : '') + '</p>'
        + (store.phone
            ? '<p>' + escHtml(i18n.phone || 'Tel.') + ': <a href="tel:' + escHtml(store.phone.replace(/\s/g, '')) + '">' + escHtml(store.phone) + '</a></p>'
            : '')
        + (store.hours ? '<p><em>' + escHtml(store.hours) + '</em></p>' : '')
        + '<div style="margin-top:10px;display:flex;gap:12px;flex-wrap:wrap;">'
        + '<a href="' + mapsUrl + '" target="_blank" rel="noopener">' + escHtml(i18n.directions || 'Itineraire') + '</a>'
        + (store.website
            ? '<a href="' + escHtml(store.website) + '" target="_blank" rel="noopener">' + escHtml(i18n.website || 'Site web') + '</a>'
            : '')
        + '</div></div>'
      );
      infoWindow.open(map, marker);
    }

    function initMap() {
      if (!mapEl || typeof google === 'undefined') { renderMapPlaceholder(); return; }

      const storesWithCoords = stores.filter(s => s.lat != null && s.lng != null);
      if (storesWithCoords.length === 0) { renderMapPlaceholder(); return; }

      const mapStyle = [
        { featureType: 'all',            elementType: 'geometry',        stylers: [{ saturation: -80 }] },
        { featureType: 'poi',            elementType: 'labels',          stylers: [{ visibility: 'off' }] },
        { featureType: 'transit',        elementType: 'labels',          stylers: [{ visibility: 'off' }] },
        { featureType: 'road',           elementType: 'labels.icon',     stylers: [{ visibility: 'off' }] },
        { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#d0ccc8' }] },
        { featureType: 'water',          elementType: 'geometry',        stylers: [{ color: '#e0ddd9' }] },
        { featureType: 'landscape',      elementType: 'geometry',        stylers: [{ color: '#f5f3f0' }] },
        { featureType: 'road.highway',   elementType: 'geometry',        stylers: [{ color: '#e8e5e0' }] },
        { featureType: 'road.local',     elementType: 'geometry',        stylers: [{ color: '#f0ede8' }] },
      ];

      map = new google.maps.Map(mapEl, {
        zoom: 8,
        center: { lat: 50.4, lng: 4.8 },
        styles: mapStyle,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
      });

      infoWindow = new google.maps.InfoWindow({ maxWidth: 300 });
      infoWindow.addListener('closeclick', () => {
        markers.forEach((m) => { if (m) m.setIcon(markerIcon(false)); });
      });

      const bounds = new google.maps.LatLngBounds();

      stores.forEach((store, i) => {
        const lat = parseFloat(store.lat);
        const lng = parseFloat(store.lng);
        if (isNaN(lat) || isNaN(lng)) { markers.push(null); return; }
        store.lat = lat; store.lng = lng;

        const pos    = { lat, lng };
        const marker = new google.maps.Marker({ position: pos, map, icon: markerIcon(false), title: store.name });
        bounds.extend(pos);
        markers.push(marker);

        marker.addListener('click', () => {
          activateCard(cards[i]);
          openInfoWindowOn(marker, store);
        });
      });

      // Auto-center: try geolocation, fallback to fit all markers
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            map.setCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude });
            map.setZoom(10);
          },
          () => { map.fitBounds(bounds, { top: 30, right: 30, bottom: 30, left: 30 }); },
          { timeout: 4000 }
        );
      } else {
        map.fitBounds(bounds, { top: 30, right: 30, bottom: 30, left: 30 });
      }
    }

    function escHtml(str) {
      return String(str || '')
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }

    function renderMapPlaceholder() {
      if (!mapEl) return;
      mapEl.innerHTML =
        '<div class="sl-map-placeholder">'
        + '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">'
        + '<path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>'
        + '<circle cx="12" cy="9" r="2.5"/></svg>'
        + '<p>Configurez une cle API Google Maps dans<br/>Parametres du theme pour afficher la carte.</p>'
        + '</div>';
    }

    // ─── Init ──────────────────────────────────────────────────────────────────

    if (typeof google !== 'undefined' && google.maps) {
      initMap();
    } else if (window.slGoogleMapsKey) {
      window._slInstances = window._slInstances || [];
      window._slInstances.push(initMap);
    } else {
      renderMapPlaceholder();
    }
  }

  window.slInitMap = function () {
    (window._slInstances || []).forEach(fn => fn());
    window._slInstances = [];
  };

  function boot() {
    qsa('.sl-root[data-section-id]').forEach(el => new StoreLocator(el));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
